import { MASCOTS } from "../components/mascots";
import { SHOTS } from "../components/shots";
import type { SiteConfig } from "../config";
import { Faq } from "../sections/Faq";
import { Features } from "../sections/Features";
import { Hero } from "../sections/Hero";
import { Identity } from "../sections/Identity";
import { Limits } from "../sections/Limits";
import { Pillars } from "../sections/Pillars";
import { Platforms } from "../sections/Platforms";
import { Security } from "../sections/Security";
import { Showcase } from "../sections/Showcase";

/**
 * The order is an argument: what it is, who you are on it, what we can prove, what it can
 * do, the two capabilities worth showing at full size, how it works, what we cannot promise,
 * where it runs, then the questions.
 *
 * The limits sit in the middle rather than at the end, because a reader who bails early
 * should still have met them.
 *
 * Roughly every other block is a screen from the product. That ratio is deliberate: the page
 * used to be text end to end, which made a messenger read like a whitepaper.
 *
 * THE NUMERALS ARE PART OF THIS ORDER. Sections 01–08 carry a mono numeral above the
 * heading; the hero and the identity band do not, because they are the opening rather than
 * an entry in the list. Showcase is the one component used twice, so its numeral has to be
 * passed in from here — which also means the sequence is visible in one place instead of
 * being hidden in eight files.
 *
 * The alternating dark bands are NOT set here: sections.css takes every second rendered
 * section, so a section that removes itself does not break the rhythm.
 */
export function Home({ config }: { config: SiteConfig }) {
  return (
    <>
      <Hero config={config} />
      <Identity />
      <Pillars />
      <Features />

      <Showcase
        id="calls"
        index="03"
        titleKey="features.calls.title"
        bodyKey="features.calls.body"
        // Holds a phone, standing left of the frames — see MASCOTS.phone for why this pose
        // is in this band and not another.
        aside={MASCOTS.phone}
        frames={[
          { shot: SHOTS.groupCall, altKey: "shots.groupCall.alt" },
          { shot: SHOTS.phoneCall, altKey: "shots.phoneCall.alt" },
        ]}
      />

      <Showcase
        id="organize"
        index="04"
        titleKey="features.organize.title"
        bodyKey="features.organize.body"
        // Sitting with a laptop, sorting things out — the one pose that says what this band
        // says (owner's choice). See MASCOTS.laptop.
        aside={MASCOTS.laptop}
        frames={[
          { shot: SHOTS.folders, altKey: "shots.folders.alt" },
          { shot: SHOTS.phoneChats, altKey: "shots.phoneChats.alt" },
        ]}
      />

      <Security />
      <Limits />
      <Platforms config={config} />
      <Faq />
    </>
  );
}
